import { waitForClientDocumentFonts } from '@/utils/clientDocumentTheme';
import { paginateDocumentFlow } from '@/utils/clientDocumentFlow';

const proposalSources = new WeakMap();

const nextFrame = () =>
	new Promise((resolve) => {
		if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
			resolve();
			return;
		}
		window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
	});

// Every page is numbered: "Page 2 of 5". From page 2, a document with a header template also
// shows its name above a thin line, as the director's Word documents do.
const paginateStructuredDocument = (root, type) => {
	const document = root.ownerDocument;
	const footerClass = `${type}-page-footer`;
	const headerClass = `${type}-page-header`;
	let blocks;
	const footerHtml = root.querySelector(`template[data-${type}-footer-template]`)?.innerHTML || '';
	const headerHtml = root.querySelector(`template[data-${type}-header-template]`)?.innerHTML.trim() || '';
	if (type === 'proposal') {
		if (!proposalSources.has(root)) {
			const originalPages = Array.from(root.querySelectorAll('.proposal-document'));
			proposalSources.set(root,
				originalPages.flatMap((page) => Array.from(page.children)
					.filter((child) => !child.classList.contains(footerClass) && !child.classList.contains(headerClass))
					.map((child) => child.cloneNode(true))),
			);
		}
		blocks = proposalSources.get(root);
	} else {
		const source = root.querySelector(`.${type}-pagination-source`);
		blocks = Array.from(source?.querySelectorAll(`:scope > .${type}-flow-block`) || []);
	}
	if (!blocks.length) return;
	root.querySelectorAll(type === 'proposal'
		? '.proposal-document'
		: `.${type}-document[data-generated-page="true"]`).forEach((page) => page.remove());

	const createPage = (pageNumber) => {
		const page = document.createElement('article');
		page.className = `pdf-gen-invoice-container ${type}-document`;
		page.style.overflowWrap = 'anywhere';
		page.dataset[`${type}Page`] = String(pageNumber);
		page.dataset.generatedPage = 'true';
		if (pageNumber > 1 && headerHtml) {
			const header = document.createElement('header');
			header.className = headerClass;
			header.innerHTML = headerHtml;
			page.appendChild(header);
		}
		const footer = document.createElement('footer');
		footer.className = footerClass;
		footer.innerHTML = `<div class="${type}-page-footer-text">${footerHtml}</div><span class="${type}-page-number">Page ${pageNumber}</span>`;
		page.appendChild(footer);
		return page;
	};
	const pages = paginateDocumentFlow({
		root,
		blocks,
		footerSelector: `.${footerClass}`,
		headerSelector: `.${headerClass}`,
		createPage,
	});

	pages.forEach((page, index) => {
		page.classList.toggle(`${type}-document--last`, index === pages.length - 1);
		const number = page.querySelector(`.${type}-page-number`);
		if (number) number.textContent = `Page ${index + 1} of ${pages.length}`;
	});
	root.dataset.paginated = 'true';
};

export const paginateClientDocumentHtml = async (container) => {
	if (!container?.querySelectorAll) return;
	await waitForClientDocumentFonts(container.ownerDocument || null);
	await nextFrame();
	for (const [documentType, type] of [
		['proposal', 'proposal'],
		['assignment-confirmation', 'assignment'],
		['quotation', 'quotation'],
		['company-record', 'record'],
	]) {
		container.querySelectorAll(`[data-client-document-autopaginate="${documentType}"]`)
			.forEach((root) => paginateStructuredDocument(root, type));
	}
	await nextFrame();
};
